import { getPaymentProcessingError } from "common/locale/labels";
import { PaymentError, PaymentType, RequestResponseBaseError, ResponseErrorCode } from "univapay-node";

import { errorCodeLabel } from "../../../common/locale/utils";
import { LOCALE_LABELS } from "../locale/labels";
import { PatchedResponseCode } from "../locale/labels/errors";

import { ChargeError } from "./ChargeError";
import { TokenError } from "./TokenError";

const convertProcessingErrorCode = (code: number, paymentType: PaymentType, message?: string) => {
    switch (code) {
        case 301:
            return ResponseErrorCode.CardNumberNotValid;

        case 302:
            return ResponseErrorCode.CardExpirationMonthInvalid;

        case 303:
            return ResponseErrorCode.CardExpirationYearInvalid;

        case 304:
            return ResponseErrorCode.CardExpired;

        case 305:
            return ResponseErrorCode.CardCVVInvalid;

        case 306:
            return paymentType === PaymentType.CARD || paymentType === PaymentType.APPLE_PAY
                ? ResponseErrorCode.CardRejected
                : paymentType === PaymentType.PAIDY
                ? PatchedResponseCode.ERROR_ALERTS_PAIDY_PAYMENT_REJECTED
                : LOCALE_LABELS.PROCESSING_ERROR;

        case 307:
            return ResponseErrorCode.CardInvalid;

        case 308:
            return ResponseErrorCode.ChargeInvalidData;

        case 309:
            return getPaymentProcessingError(message);

        case 310:
            return ResponseErrorCode.InvalidUserData;

        case 311:
            return ResponseErrorCode.TooManyChargeRequests;

        case 312:
            return ResponseErrorCode.CancelUnavailable;

        case 313:
            return ResponseErrorCode.ChargeExpired;

        case 314:
            return ResponseErrorCode.SeizeCard;

        case 315:
            return ResponseErrorCode.ContactBank;

        case 316:
            return ResponseErrorCode.LastNameRequired;

        case 317:
            return ResponseErrorCode.PartialCaptureNotSupported;

        case 318:
            return ResponseErrorCode.PartialRefundNotSupported;

        case 319:
            return ResponseErrorCode.FraudSuspected;

        case 320:
            return ResponseErrorCode.BankSystemFailure;

        case 321:
            return ResponseErrorCode.DynamicDescriptorNotSupported;

        case 322:
            return ResponseErrorCode.PaymentCodeInvalid;

        case 323:
            return ResponseErrorCode.PaymentCodeExpired;

        case 324:
            return ResponseErrorCode.PaymentCodeAlreadyUsed;

        case 325:
            return ResponseErrorCode.PaymentCodeStillInUse;

        case 326:
            return ResponseErrorCode.RejectedHighRisk;

        case 327:
            return ResponseErrorCode.ConfirmationPeriodExpired;

        case 328:
            return ResponseErrorCode.RevertFailed;

        case 329:
            return ResponseErrorCode.RefundFailed;

        case 330:
            return ResponseErrorCode.PaymentWalletInsufficientFunds;

        case 331:
            return ResponseErrorCode.InvalidMetadataFieldValue;

        case 332:
            return ResponseErrorCode.CrossBorderNotAcceptedMissingId;

        case 333:
            return ResponseErrorCode.CrossBorderNotAcceptedMissingPhoneNumber;

        case 334:
            return ResponseErrorCode.CrossBorderNotAcceptedUnacceptedPaymentMethod;

        case 335:
            return ResponseErrorCode.CrossBorderNotAcceptedMissingName;

        case 336:
            return ResponseErrorCode.LimitExceededForPaymentType;

        case 337:
            return ResponseErrorCode.LimitExceededForMerchant;

        case 338:
            return ResponseErrorCode.TransactionNotFound;

        case 339:
            return ResponseErrorCode.DuplicateTransaction;

        case 340:
            return ResponseErrorCode.PaymentWalletRejected;

        case 341:
            return ResponseErrorCode.InsufficientMerchantInformation;

        case 342:
            return ResponseErrorCode.CrossBorderNotAcceptedUnacceptedCurrency;

        case 343:
            return ResponseErrorCode.GatewayServerError;

        case 344:
            return ResponseErrorCode.PaymentMethodTemporarilyUnavailable;

        case 345:
            return ResponseErrorCode.PaymentCanceled;

        case 346:
            return ResponseErrorCode.ExceededPendingThreshold;

        case 347:
            return PatchedResponseCode.VIRTUAL_BANK_ACCOUNT_AWAITING_PERIOD_EXPIRED;

        case 348:
            return PatchedResponseCode.VIRTUAL_BANK_ACCOUNT_HAS_NON_ZERO_BALANCE;

        case 349:
            return ResponseErrorCode.ChargeAmountTooLow;

        case 350:
            return PatchedResponseCode.NO_PAYMENT_METHOD_AVAILABLE;

        case 351:
            return LOCALE_LABELS.DISPUTED_TRANSACTION;

        case 352:
            return PatchedResponseCode.MERCHANT_ACCOUNT_SUSPENDED;

        case 353:
            return PatchedResponseCode.INVALID_USER_AUTHORIZATION;

        case 354:
            return PatchedResponseCode.INVALID_AUTH_DATA;

        case 355:
            return LOCALE_LABELS.PAYMENT_TYPE_NOT_SUPPORTED;

        case 356:
            return LOCALE_LABELS.CARD_NOT_ENROLLED_IN_THREE_DS;

        case 357:
            return PatchedResponseCode.CARD_IS_ENROLLED_WITHOUT_THREE_DS;

        case 358:
            return LOCALE_LABELS.THREE_DS_COULD_NOT_BE_COMPLETED;

        case 359:
            return ResponseErrorCode.CardRejected;

        case 360:
            return PatchedResponseCode.NO_TERMINAL_ID_AVAILABLE;

        case 361:
            return PatchedResponseCode.TERMINAL_ID_SEQUENCE_IS_FULL;

        case 362:
            return LOCALE_LABELS.INSTALLMENT_CYCLES_NOT_AVAILABLE_FOR_METHOD;

        case 363:
            return LOCALE_LABELS.IDENTITY_VERIFICATION_INCOMPLETE;

        case 364:
            return PatchedResponseCode.TOKENIZED_CARD_DATA_NOT_FOUND;

        case 403:
            return ResponseErrorCode.CardLocked;

        case 420:
            return PatchedResponseCode.SERVICE_RESTRICTED;

        case 504:
            return PatchedResponseCode.NO_GATEWAY_AVAILABLE_TO_PROCESS_REQUEST;

        case 505:
            return PatchedResponseCode.THREE_DS_ISSUER_TOKEN_CREATION_FAILURE;

        default:
            return null;
    }
};

type ErrorType = RequestResponseBaseError | TokenError | PaymentError;

export const errorLabel = (error: ErrorType, paymentType: PaymentType): string => {
    if (!error) {
        return ResponseErrorCode.UnknownError;
    }

    if (error instanceof RequestResponseBaseError) {
        const { errorResponse } = error;

        // Handling of custom labels overwrite
        switch (errorResponse.code) {
            case ResponseErrorCode.InvalidCardTypeForCapture:
                return PatchedResponseCode.ERRORS_ALERTS_CAPTURE_CREDIT_ONLY;

            default:
                return errorCodeLabel(errorResponse.code);
        }
    }

    if (error instanceof TokenError) {
        return convertProcessingErrorCode(error.code as number, paymentType, error.message) || error.message;
    }

    // For charge status and token three DS errors we will have a sub error
    if (error instanceof ChargeError && error.error) {
        return convertProcessingErrorCode(error.error.code, paymentType, error.message) || error.error.message;
    }

    return convertProcessingErrorCode(error.code, paymentType, error.message) || ResponseErrorCode.UnknownError;
};

export const getErrorHttpCode = (error: ErrorType): number | null => {
    if (!error) {
        return null;
    }

    return error instanceof RequestResponseBaseError ? error.errorResponse.httpCode : Number(error["code"]) || null;
};
